import axios from '@/plugins/axios';
import utils from '@/stores/utils';
import store from '@/stores/store';
import { resolvePermissionCheck } from '@/utils/groupPermissions';
const { getStorageKey, loadFiltersFromStorage, saveFiltersToStorage } = utils;

const DEFAULT_FILTERS = { offset: 0, limit: 20, ordering: '-creation_date' };

const createState = (options = {}) => ({
    orderitems: [],
    orderitems_url: null,
    orderitems_filters: DEFAULT_FILTERS,
    orderitems_base_filters: {},
    orderitems_count: 0,
    orderitems_total_count: null,
    orderitems_loaded: false,
    orderitems_loading: false,
    orderitems_loading_errors: null,

    orderitems_stats: {},
    orderitems_stats_loaded: false,
    orderitems_stats_loading: false,
    orderitems_stats_loading_errors: null,
    cancelTokenSource: null,
    cancelTokenSourceStats: null,
    _storageOptions: options || {},
});

const mutations = {
    updateOrderItems(state, orderitems) {
        state.orderitems = orderitems;
        state.orderitems_loaded = true;
        state.orderitems_loading = false;
        state.orderitems_loading_errors = null;
    },
    updateOrderItemsUrl(state, orderitems_url) {
        state.orderitems_url = orderitems_url;
    },
    updateOrderItemsCount(state, orderitems_count) {
        state.orderitems_count = orderitems_count;
    },
    updateOrderItemsTotalCount(state, orderitems_total_count) {
        state.orderitems_total_count = orderitems_total_count;
    },
    updateOrderItemsBaseFilters(state, orderitems_base_filters) {
        state.orderitems_base_filters = orderitems_base_filters || {};
    },
    updateOrderItemsFilters(state, orderitems_filters) {
        state.orderitems_filters = orderitems_filters;
        const { storageKey, fieldsToSave } = state._storageOptions;
        if (storageKey) {
            saveFiltersToStorage(storageKey, state.orderitems_filters, fieldsToSave);
        }
    },
    updateOrderItemsLoading(state, orderitems_loading) {
        state.orderitems_loading = orderitems_loading;
    },
    updateOrderItemsLoadingErrors(state, orderitems_loading_errors) {
        state.orderitems_loading_errors = orderitems_loading_errors;
    },


    updateOrderItemsStats(state, orderitems_stats) {
        state.orderitems_stats = orderitems_stats;
        state.orderitems_stats_loaded = true;
    },
    updateOrderItemsStatsLoading(state, loading) {
        state.orderitems_stats_loading = loading;
    },
    updateOrderItemsStatsLoadingErrors(state, errors) {
        state.orderitems_stats_loading_errors = errors;
    },
    updateCancelTokenSource(state, source) {
        state.cancelTokenSource = source;
    },
    updateCancelTokenSourceStats(state, source) {
        state.cancelTokenSourceStats = source;
    },
    updateStorageOptions(state, storage_options) {
        state._storageOptions = storage_options;
    },

};

const actions = {

    async fetchOrderItems({ commit, dispatch, state }, params) {
        const permissions = store.getters['session/current_user_permissions'];
        if (!resolvePermissionCheck(permissions, "order.view_orderitem")) {
            return;
        }

        // Cancel any pending request
        if (state.cancelTokenSource) {
            state.cancelTokenSource.cancel("New fetch started");
        }

        // Create new cancel token source
        const source = axios.CancelToken.source();
        commit('updateCancelTokenSource', source);

        commit('updateOrderItemsLoading', true);
        commit('updateOrderItemsLoadingErrors', null);

        try {
            const response = await axios.get(state.orderitems_url, {
                params: state.orderitems_filters,
                cancelToken: source.token,
            });
            commit('updateOrderItemsCount', response.data.count);
            commit('updateOrderItems', response.data.results);
            dispatch('fetchOrderItemsStats');
            return response;
        } catch (xhr_error) {
            // Ignore cancelled requests
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('updateOrderItemsLoadingErrors', error.details);
            throw error;
        } finally {
            commit('updateOrderItemsLoading', false);
        }
    },


    async fetchOrderItemsTotal({ commit, state }) {
        const permissions = store.getters['session/current_user_permissions'];
        if (!resolvePermissionCheck(permissions, "order.view_orderitem")) {
            commit('updateOrderItemsTotalCount', 0);
            return 0;
        }
        try {
            const response = await axios.get(state.orderitems_url, {
                params: { ...state.orderitems_base_filters, limit: 0 },
            });
            const count = response.data.count ?? 0;
            commit('updateOrderItemsTotalCount', count);
            return count;
        } catch (xhr_error) {
            if (axios.isCancel(xhr_error)) {
                return;
            }
            commit('updateOrderItemsTotalCount', 0);
            throw utils.handleError(xhr_error);
        }
    },

    async fetchOrderItemsStats({ commit, dispatch, state }, params) {
        if (store.getters['session/current_user_permissions'].indexOf("order.view_allocation") == -1) {
            return;
        }

        // Cancel any pending request
        if (state.cancelTokenSourceStats) {
            state.cancelTokenSourceStats.cancel("New stats fetch started");
        }

        // Create new cancel token source
        const source = axios.CancelToken.source();
        commit('updateCancelTokenSourceStats', source);

        commit('updateOrderItemsStatsLoading', true);
        commit('updateOrderItemsStatsLoadingErrors', null);

        try {
            const response = await axios.get(`${state.orderitems_url}stats/`, {
                params: state.orderitems_filters,
                cancelToken: source.token,
            });
            commit('updateOrderItemsStatsLoading', false);
            commit('updateOrderItemsStats', response.data);
            return response;
        } catch (xhr_error) {
            // Ignore cancelled requests
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('updateOrderItemsStatsLoadingErrors', error.details);
            commit('updateOrderItemsStatsLoading', false);
            throw error;
        }
    },

    async init({ commit, dispatch, state }, params) {
        commit('updateOrderItems', []);
        commit('updateOrderItemsTotalCount', null);
        // Identity filters (e.g. product) used to count the unfiltered total.
        commit('updateOrderItemsBaseFilters', params?.filters || {});
        
        // Update storage options if provided
        if (params?.storageOptions !== undefined) {
            await commit('updateStorageOptions', params.storageOptions);
        }
        
        // Load filters from localStorage if storage is configured
        await commit('updateOrderItemsFilters', { 
            ...DEFAULT_FILTERS, 
            ...(state._storageOptions?.storageKey ? loadFiltersFromStorage(state._storageOptions?.storageKey, {}) : {}),
            ...(params?.filters || {})
        });
        
        if (params && params.url) {
            commit('updateOrderItemsUrl', params.url);
        } else if (params.type == 'customerorder') {
            commit('updateOrderItemsUrl', "/api/customerorderitems/");
        } else if (params.type == 'providerorder') {
            commit('updateOrderItemsUrl', "/api/providerorderitems/");
        } else {
            throw "Invalid orderitems type";
        }
    },
};


export default (options = {}) => ({
    namespaced: true,
    state: createState(options),
    mutations,
    actions
});
